/**
 * Auth API Service
 */

import axios, { type AxiosError } from 'axios';
import { BaseApiService, logger } from '@metlas/shared';
import type {
  User,
  LoginResponse,
  CurrentUserResponse,
  MessageResponse,
  AuthErrorType,
} from '../types/auth';

interface ApiErrorResponse {
  error: string;
  errorCode?: string;
}

function createAuthError(type: AuthErrorType, message: string): Error & { type: AuthErrorType } {
  const error = new Error(message) as Error & { type: AuthErrorType };
  error.type = type;
  error.name = 'AuthError';
  return error;
}

class AuthApiService extends BaseApiService {
  constructor() {
    super('Auth');
  }

  async login(username: string, password: string): Promise<LoginResponse> {
    try {
      const data = await this.post<LoginResponse>('/api/v1/auth/cs-advisor/login', {
        username,
        password,
      });
      return data;
    } catch (error) {
      return this.handleAuthError(error as AxiosError, 'Login');
    }
  }

  /**
   * Request a password-reset link. Resolves with the success message.
   * On an unknown/invalid email the backend responds 400 and this rejects
   * with that message (per the "say invalid email" requirement).
   */
  async forgotPassword(email: string): Promise<string> {
    try {
      const data = await this.post<MessageResponse>(
        '/api/v1/auth/cs-advisor/forgot-password',
        { email }
      );
      return data.data.message;
    } catch (error) {
      return this.handleAuthError(error as AxiosError, 'Password reset request');
    }
  }

  /**
   * Consume a reset token and set a new password. Resolves with the success
   * message; rejects with the backend message on invalid/expired/used tokens.
   */
  async resetPassword(token: string, newPassword: string): Promise<string> {
    try {
      const data = await this.post<MessageResponse>(
        '/api/v1/auth/cs-advisor/reset-password',
        { token, newPassword }
      );
      return data.data.message;
    } catch (error) {
      return this.handleAuthError(error as AxiosError, 'Password reset');
    }
  }

  async logout(): Promise<void> {
    try {
      await this.post('/api/v1/auth/cs-advisor/logout');
    } catch (error) {
      logger.error('[AuthAPI] Logout failed:', error);
      throw createAuthError('NETWORK_ERROR' as AuthErrorType, 'Failed to logout');
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const data = await this.get<CurrentUserResponse>('/api/v1/auth/cs-advisor/me');
      return data.data?.user ?? null;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        return null;
      }
      logger.error('[AuthAPI] Failed to get current user:', error);
      return null;
    }
  }

  private handleAuthError(error: AxiosError, operation: string): never {
    logger.error(`[AuthAPI] ${operation} failed:`, error);
    if (!error.response) {
      throw createAuthError(
        'NETWORK_ERROR' as AuthErrorType,
        'Network error. Please check your connection.'
      );
    }
    const status = error.response.status;
    const errorData = (error.response.data as ApiErrorResponse) || {};
    const message = errorData.error || `${operation} failed. Please try again.`;
    if (status === 401 || status === 422) {
      throw createAuthError('INVALID_CREDENTIALS' as AuthErrorType, message);
    }
    throw createAuthError('UNKNOWN_ERROR' as AuthErrorType, message);
  }
}

export const authApiService = new AuthApiService();
